// Fire Risk advisory PDF layout pieces, drawn to match "Fire Risk-Eng.svg".
// Fire-risk specific on purpose: the shared helpers in reportTemplate.js are
// used by other hazards whose reference designs differ slightly.
import { COLORS } from "../../../common/reportTemplate/reportTemplate.js";

export const PAGE_W = 210;
export const PAGE_H = 297;
export const SIDE = 10;

// Footer bar geometry (mm). The "Page x of y" label sits just under the bar.
export const FR_FOOTER_Y = 266;
export const FR_FOOTER_H = 21;
export const FR_CONTENT_BOTTOM = FR_FOOTER_Y - 4;

const hex = (h) => {
  const s = h.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(s.substring(i, i + 2), 16));
};
const fill = (pdf, h) => pdf.setFillColor(...hex(h));
const ink = (pdf, h) => pdf.setTextColor(...hex(h));

// ---------------------------------------------------------------------------
// Header — positions, sizes and colour measured from "Fire Risk-Eng.svg"
// (A4 mm). The reference uses a condensed display face that Helvetica lacks,
// so each line is sized by its cap height and horizontally compressed (the
// PDF "Tz" operator) to the reference's measured text width.
// ---------------------------------------------------------------------------
const HEADER_NAVY = "#020187";
// Subtitle and issued-by lines use this lighter blue; the title keeps the navy.
const HEADER_BLUE = "#093585";
export const FR_HEADER_H = 29.5;

function condensedLine(pdf, text, { centerX, baseline, capH, targetW, weight, color }) {
  const size = capH / 0.718 / 0.3528; // cap height (mm) -> font size (pt)
  pdf.setFont("helvetica", "bold").setFontSize(size);
  const natural = pdf.getTextWidth(text);
  const scale = Math.min(1, targetW / natural);
  const left = centerX - (natural * scale) / 2;
  // Thin same-colour stroke thickens Helvetica Bold toward the reference's heavy weight.
  ink(pdf, color);
  pdf.setDrawColor(...hex(color)).setLineWidth(weight || 0.01);
  pdf.internal.write(`${(scale * 100).toFixed(2)} Tz`);
  pdf.text(text, left, baseline, weight ? { renderingMode: "fillThenStroke" } : undefined);
  pdf.internal.write("100 Tz");
}

/** Orange header band with both emblems; `logos` from preloadReportLogos(). */
export function drawFireHeader(pdf, [apcLogo, dnmgLogo], { issuedAt, title = "TIMOR LESTE FIRE RISK ADVISORY" }) {
  fill(pdf, COLORS.headerOrange);
  pdf.rect(0, 0, PAGE_W, FR_HEADER_H, "F");

  // Logo PNGs carry a little transparent padding, so the image box is drawn
  // slightly larger than the visible emblem measured in the SVG.
  if (apcLogo) {
    try { pdf.addImage(apcLogo, "PNG", 3.4, 0.9, 26, 26); } catch (e) { /* header text still renders */ }
  }
  if (dnmgLogo) {
    try { pdf.addImage(dnmgLogo, "PNG", 179.1, 0.3, 28.7, 28.7); } catch (e) { /* header text still renders */ }
  }

  // 92.9 mm is the SVG's width for the 30-character fire title; longer titles
  // scale with their length, capped so they stay clear of the emblems.
  condensedLine(pdf, title,
    { centerX: 102.95, baseline: 10.6, capH: 6.7, targetW: Math.min(140, (92.9 * title.length) / 30), weight: 0.3, color: HEADER_NAVY });
  condensedLine(pdf, "DISASTER RISK REDUCTION DECISION SUPPORT SYSTEM",
    { centerX: 108, baseline: 17.8, capH: 3.5, targetW: 138.2, weight: 0, color: HEADER_BLUE });
  condensedLine(pdf, `ISSUED BY: DNMG-APC * ${issuedAt}`,
    { centerX: 108.6, baseline: 24.7, capH: 3.3, targetW: 112.8, weight: 0, color: HEADER_BLUE });
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(0, 0, 0);
  pdf.setLineWidth(0.2).setDrawColor(0, 0, 0);
}

// "25 AUGUST 2026 * 10:35 TLT"
export const formatIssuedAt = (date = new Date()) => {
  const parts = {};
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dili", day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
  const hh = parts.hour === "24" ? "00" : parts.hour;
  return `${parts.day} ${parts.month.toUpperCase()} ${parts.year} * ${hh}:${parts.minute} TLT`;
};

// Two differently coloured bold runs, centred as one line.
export function drawTwoToneHeading(pdf, y, left, right, size = 12) {
  pdf.setFont("helvetica", "bold").setFontSize(size);
  const lw = pdf.getTextWidth(left);
  const rw = pdf.getTextWidth(right);
  const x = (PAGE_W - lw - rw) / 2;
  ink(pdf, COLORS.red);
  pdf.text(left, x, y);
  ink(pdf, COLORS.riskLow);
  pdf.text(right, x + lw, y);
  pdf.setTextColor(0, 0, 0);
}

// Flame glyph from cubic curves inside a unit box at (x, y) of size s.
function flame(pdf, x, y, s, color, scale = 1) {
  const w = s * scale;
  const ox = x + (s - w) / 2;
  const oy = y + (s - w);
  const P = (a, b) => [ox + a * w, oy + b * w];
  const segs = [
    [[0.15, 1], [0.05, 0.7], [0.2, 0.45]],
    [[0.25, 0.55], [0.3, 0.6], [0.35, 0.6]],
    [[0.3, 0.3], [0.45, 0.1], [0.55, 0]],
    [[0.6, 0.25], [0.95, 0.45], [0.95, 0.7]],
    [[0.95, 0.9], [0.75, 1], [0.5, 1]],
  ];
  let [cx, cy] = P(0.5, 1);
  const lines = segs.map((seg) => {
    const [c1, c2, e] = seg.map(([a, b]) => P(a, b));
    const rel = [c1[0] - cx, c1[1] - cy, c2[0] - cx, c2[1] - cy, e[0] - cx, e[1] - cy];
    [cx, cy] = e;
    return rel;
  });
  const [sx, sy] = P(0.5, 1);
  fill(pdf, color);
  pdf.lines(lines, sx, sy, [1, 1], "F", true);
}

/** Light-green summary card with dashed flame badge and a boxed level readout. */
// Water drop glyph (cubic curves in a unit box), for the flash-flood variant.
function drop(pdf, x, y, s, color, scale = 1) {
  const w = s * scale;
  const ox = x + (s - w) / 2;
  const oy = y + (s - w);
  const P = (a, b) => [ox + a * w, oy + b * w];
  const segs = [
    [[0.56, 0.25], [0.92, 0.5], [0.92, 0.7]],
    [[0.92, 0.9], [0.74, 1], [0.5, 1]],
    [[0.26, 1], [0.08, 0.9], [0.08, 0.7]],
    [[0.08, 0.5], [0.44, 0.25], [0.5, 0]],
  ];
  let [cx, cy] = P(0.5, 0);
  const lines = segs.map((seg) => {
    const [c1, c2, e] = seg.map(([a, b]) => P(a, b));
    const rel = [c1[0] - cx, c1[1] - cy, c2[0] - cx, c2[1] - cy, e[0] - cx, e[1] - cy];
    [cx, cy] = e;
    return rel;
  });
  const [sx, sy] = P(0.5, 0);
  fill(pdf, color);
  pdf.lines(lines, sx, sy, [1, 1], "F", true);
}

// Two-armed spiral (cyclone), for the cyclone variant.
function spiral(pdf, cx, cy) {
  pdf.setLineCap("round");
  [["#1E6FD9", 0], ["#7DC4FF", Math.PI]].forEach(([color, phase]) => {
    pdf.setDrawColor(...hex(color)).setLineWidth(1.3);
    let prev = null;
    for (let i = 0; i <= 56; i++) {
      const a = phase + i * 0.22;
      const r = 0.5 * (i * 0.22) + 0.6;
      const pt = [cx + r * Math.cos(a), cy + r * Math.sin(a)];
      if (prev) pdf.line(prev[0], prev[1], pt[0], pt[1]);
      prev = pt;
    }
  });
  pdf.setLineCap("butt");
  fill(pdf, "#1E6FD9");
  pdf.circle(cx, cy, 1.5, "F");
}

// Mountain slope with tumbling rocks, for the landslide variant.
function slope(pdf, cx, cy) {
  const tri = (a, b, c, color) => { fill(pdf, color); pdf.triangle(a[0], a[1], b[0], b[1], c[0], c[1], "F"); };
  tri([cx - 1, cy - 7], [cx - 8, cy + 6.5], [cx + 7, cy + 6.5], "#A9743F");
  tri([cx - 1, cy - 7], [cx + 7, cy + 6.5], [cx - 1, cy + 6.5], "#7A4E27");
  fill(pdf, "#4A4A4A");
  [[cx + 3.2, cy - 1.2, 1.0], [cx + 5.6, cy + 2.4, 1.2], [cx + 1.6, cy + 3.9, 0.9]].forEach(([x, y, r]) => pdf.circle(x, y, r, "F"));
}

export function drawFireSummaryCard(pdf, y, {
  forecastDate,
  levelText,
  levelSub = "Forecast",
  dateLabel = "Forecast data",
  height = 34,
  heading = "FIRE RISK TODAY",
  description = "Forecast assesses the likelihood of fire occurrence across Timor-Leste",
  icon = "flame",
}) {
  const x = 5;
  const w = PAGE_W - 10;
  fill(pdf, COLORS.cardGreen);
  pdf.roundedRect(x, y, w, height, 4, 4, "F");

  // dashed ring + flame
  const cx = x + 17;
  const cy = y + height / 2;
  const r = 10.5;
  pdf.setDrawColor(0, 0, 0).setLineWidth(1.4);
  const n = 14;
  for (let i = 0; i < n; i++) {
    const a1 = (i * 360) / n;
    const a2 = a1 + 360 / n / 2;
    const rad = (d) => (d * Math.PI) / 180;
    pdf.line(
      cx + r * Math.cos(rad(a1)), cy + r * Math.sin(rad(a1)),
      cx + r * Math.cos(rad(a2)), cy + r * Math.sin(rad(a2))
    );
  }
  if (icon === "cyclone") {
    spiral(pdf, cx, cy);
  } else if (icon === "slope") {
    slope(pdf, cx, cy);
  } else if (icon === "drop") {
    drop(pdf, cx - 6, cy - 7.5, 14, "#1E6FD9");
    drop(pdf, cx - 6, cy - 7.5, 14, "#7DC4FF", 0.5);
  } else {
    flame(pdf, cx - 6, cy - 7.5, 14, "#E8261B");
    flame(pdf, cx - 6, cy - 7.5, 14, "#FFB400", 0.55);
  }

  const tx = x + 36;
  ink(pdf, "#000000");
  pdf.setFont("helvetica", "bold").setFontSize(16);
  pdf.text(heading, tx, y + 12);
  pdf.setFontSize(11);
  pdf.text(`${dateLabel} : ${forecastDate}`, tx, y + 19.5);
  ink(pdf, "#1A3A8F");
  pdf.setFontSize(9.5);
  pdf.text(description, tx, y + 27);

  // Right readout box
  const bw = 36;
  const bh = height - 12;
  const bx = x + w - bw - 5;
  const by = y + 6;
  pdf.setDrawColor(210, 210, 210).setLineWidth(0.8);
  fill(pdf, COLORS.white);
  pdf.roundedRect(bx, by, bw, bh, 3, 3, "FD");
  ink(pdf, COLORS.riskLow);
  pdf.setFont("helvetica", "bold").setFontSize(11);
  pdf.text(levelText, bx + bw / 2, by + bh / 2 - 1, { align: "center" });
  pdf.text(levelSub, bx + bw / 2, by + bh / 2 + 5, { align: "center" });
  pdf.setLineWidth(0.2).setDrawColor(0, 0, 0);
  pdf.setTextColor(0, 0, 0);
  return y + height;
}

/** Four tall cards, each with a heading and a big coloured count circle. */
export function drawRiskCountCards(pdf, y, cards, { height = 46, gap = 5.3, width = 35 } = {}) {
  const total = cards.length * width + (cards.length - 1) * gap;
  const startX = (PAGE_W - total) / 2;
  cards.forEach((c, i) => {
    const x = startX + i * (width + gap);
    fill(pdf, c.bg);
    pdf.roundedRect(x, y, width, height, 4, 4, "F");
    ink(pdf, "#000000");
    pdf.setFont("helvetica", "bold").setFontSize(13);
    pdf.text(c.heading, x + width / 2, y + 9, { align: "center" });
    const d = 27.5;
    const cx = x + width / 2;
    const cy = y + 12 + d / 2 + 1;
    fill(pdf, c.circle);
    pdf.circle(cx, cy, d / 2, "F");
    ink(pdf, "#000000");
    const num = String(c.count);
    pdf.setFontSize(num.length > 3 ? 20 : 28);
    pdf.text(num, cx, cy + (num.length > 3 ? 2.6 : 3.8), { align: "center" });
  });
  return y + height;
}

const fitText = (pdf, text, maxW, size) => {
  let s = size;
  pdf.setFontSize(s);
  while (s > 6 && pdf.getTextWidth(text) > maxW) { s -= 0.5; pdf.setFontSize(s); }
};

/** Blue "IMPORTANT" bar with red alert disc, plus the centred page label. */
export function drawFireFooter(pdf, { message, pageLabel }) {
  const x = SIDE;
  const w = PAGE_W - 2 * SIDE;
  const y = FR_FOOTER_Y;
  fill(pdf, COLORS.footerBlue);
  pdf.roundedRect(x, y, w, FR_FOOTER_H, 4, 4, "F");

  const cx = x + 27;
  const cy = y + FR_FOOTER_H / 2;
  fill(pdf, COLORS.riskExtreme);
  pdf.circle(cx, cy, 8.4, "F");
  fill(pdf, COLORS.white);
  pdf.roundedRect(cx - 0.9, cy - 5, 1.8, 6.4, 0.8, 0.8, "F");
  pdf.circle(cx, cy + 4, 1.1, "F");

  const tx = x + 40;
  const maxW = x + w - tx - 4;
  pdf.setFont("helvetica", "bold");
  ink(pdf, COLORS.riskHigh);
  pdf.setFontSize(11);
  pdf.text("IMPORTANT", tx, y + 5.8);
  ink(pdf, COLORS.white);
  const msg = String(message).toUpperCase();
  fitText(pdf, msg, maxW, 10.5);
  pdf.text(msg, tx + 0.8, y + 10.8);
  ink(pdf, COLORS.red);
  const emergency = "In emergency please call : 115 (APC); 112 (PNTL); 110 (SNAEM)";
  fitText(pdf, emergency, maxW, 8.8);
  pdf.text(emergency, tx + 1, y + 15.2);
  ink(pdf, COLORS.riskHigh);
  const contact = "drr.dnmg.gov.tl  *  info@dnmg.gov.tl  *  +670 7709 4893";
  fitText(pdf, contact, maxW, 8.4);
  pdf.text(contact, tx + 5, y + 19);

  pdf.setFont("helvetica", "bolditalic").setFontSize(9);
  ink(pdf, "#000000");
  pdf.text(pageLabel, PAGE_W / 2, y + FR_FOOTER_H + 5, { align: "center" });
  pdf.setFont("helvetica", "normal");
}
